import React, { useState } from 'react';
import { COURSES } from '../data/coursesData';
import { Calculator, CreditCard, CheckCircle2, ArrowRight } from 'lucide-react';

export default function FeeCalculator({ onApplyClick }) {
  const [selectedCourseId, setSelectedCourseId] = useState('msc-ds');
  const [tenureMonths, setTenureMonths] = useState(24);

  const course = COURSES.find(c => c.id === selectedCourseId) || COURSES[0];

  const totalFee = course.totalFee;
  const semFee = course.feePerSemester;
  const numSemesters = course.durationYears * 2;
  const estimatedMonthlyEMI = Math.round(totalFee / tenureMonths);

  return (
    <section id="calculator" style={{
      padding: '5rem 0',
      backgroundColor: '#0F2537',
      color: '#FFFFFF',
      position: 'relative'
    }}>
      <div className="container">

        {/* Section Header */}
        <div style={{ textAlign: 'center', maxWidth: '720px', margin: '0 auto 3.5rem auto' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem',
            backgroundColor: 'rgba(212, 175, 55, 0.12)',
            border: '1px solid rgba(212, 175, 55, 0.4)',
            color: '#FEF08A',
            fontWeight: 600,
            fontSize: '0.82rem',
            padding: '0.35rem 0.9rem',
            borderRadius: '9999px',
            marginBottom: '0.75rem'
          }}>
            <Calculator size={14} color="#D4AF37" /> Transparent & Flexible Fee Options
          </div>
          <h2 style={{ fontSize: '2.3rem', fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.02em' }}>
            Interactive Fee & Monthly EMI Calculator
          </h2>
          <p style={{ fontSize: '1.05rem', color: '#94A3B8', marginTop: '0.5rem', lineHeight: 1.6 }}>
            Calculate your semester fee breakdown and flexible zero-cost monthly EMI options without hidden charges.
          </p>
        </div>

        {/* Calculator Widget Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '2.5rem',
          alignItems: 'center'
        }}>
          
          {/* Controls Column */}
          <div className="glass-card-dark" style={{ padding: '2.25rem' }}>
            <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#FEF08A', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <CreditCard size={20} color="#D4AF37" /> Select Program & Tenure
            </h3>

            {/* Select Program */}
            <div style={{ marginBottom: '1.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 600, color: '#CBD5E1', marginBottom: '0.5rem' }}>
                1. Select Online Degree Program
              </label>
              <select
                className="form-input"
                value={selectedCourseId}
                onChange={(e) => setSelectedCourseId(e.target.value)}
                style={{
                  backgroundColor: '#07131E',
                  color: '#FFFFFF',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  fontSize: '0.95rem',
                  padding: '0.85rem'
                }}
              >
                {COURSES.map(c => (
                  <option key={c.id} value={c.id} style={{ backgroundColor: '#0F2537' }}>
                    {c.name} ({c.durationYears} Yrs - ₹{c.totalFee.toLocaleString('en-IN')})
                  </option>
                ))}
              </select>
            </div>

            {/* EMI Tenure Slider */}
            <div style={{ marginBottom: '1.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label style={{ fontSize: '0.88rem', fontWeight: 600, color: '#CBD5E1' }}>
                  2. Choose EMI Tenure (Months)
                </label>
                <span style={{ fontSize: '1rem', fontWeight: 800, color: '#FEF08A' }}>
                  {tenureMonths} Months
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' }}>
                {[12, 18, 24, 36].map(m => (
                  <button
                    key={m}
                    onClick={() => setTenureMonths(m)}
                    style={{
                      padding: '0.65rem 0.5rem',
                      borderRadius: '8px',
                      fontSize: '0.88rem',
                      fontWeight: tenureMonths === m ? 700 : 500,
                      backgroundColor: tenureMonths === m ? '#D4AF37' : 'rgba(255, 255, 255, 0.08)',
                      color: tenureMonths === m ? '#0F172A' : '#FFFFFF',
                      border: tenureMonths === m ? 'none' : '1px solid rgba(255, 255, 255, 0.15)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {m} M
                  </button>
                ))}
              </div>
            </div>

            {/* Inclusions checklist */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', paddingTop: '1rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
              {[
                "Includes all study materials & 24/7 LMS access",
                "Includes digital library & video lecture archives",
                "Zero interest rates & zero processing fees available",
                "Pay per semester option also available"
              ].map((item, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', color: '#94A3B8' }}>
                  <CheckCircle2 size={14} color="#10B981" /> {item}
                </div>
              ))}
            </div>
          </div>

          {/* Results Summary Card */}
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '20px',
            padding: '2.25rem',
            color: '#0F172A',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)'
          }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#2563EB', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Financial Breakdown
            </div>
            <h4 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0F2537', marginTop: '0.2rem', marginBottom: '1.5rem' }}>
              {course.name}
            </h4>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem', marginBottom: '1.75rem' }}>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '0.75rem', borderBottom: '1px solid #E2E8F0' }}>
                <span style={{ fontSize: '0.9rem', color: '#64748B' }}>Total Program Fee ({numSemesters} Semesters)</span>
                <span style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0F2537' }}>
                  ₹{totalFee.toLocaleString('en-IN')}
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '0.75rem', borderBottom: '1px solid #E2E8F0' }}>
                <span style={{ fontSize: '0.9rem', color: '#64748B' }}>Per Semester Payment</span>
                <span style={{ fontSize: '1.05rem', fontWeight: 700, color: '#059669' }}>
                  ₹{semFee.toLocaleString('en-IN')}
                </span>
              </div>

              <div style={{
                backgroundColor: '#ECFDF5',
                padding: '1.1rem',
                borderRadius: '12px',
                border: '1px solid #A7F3D0',
                display: 'flex',
                justify: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{ fontSize: '0.82rem', color: '#047857', fontWeight: 700 }}>ESTIMATED MONTHLY EMI</div>
                  <div style={{ fontSize: '0.75rem', color: '#065F46' }}>for {tenureMonths} Months Tenure</div>
                </div>
                <div style={{ fontSize: '1.65rem', fontWeight: 800, color: '#047857' }}>
                  ₹{estimatedMonthlyEMI.toLocaleString('en-IN')}<span style={{ fontSize: '0.9rem', fontWeight: 500 }}>/mo</span>
                </div>
              </div>

            </div>

            {/* Financial Partner Badges */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 600, marginBottom: '0.5rem' }}>
                OFFICIAL FINANCING PARTNERS:
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {['HDFC Bank', 'ICICI Bank', 'Razorpay Capital', 'Bajaj Finserv'].map((partner, pIdx) => (
                  <span key={pIdx} style={{
                    fontSize: '0.75rem',
                    backgroundColor: '#F1F5F9',
                    color: '#334155',
                    padding: '0.25rem 0.6rem',
                    borderRadius: '6px',
                    fontWeight: 600
                  }}>
                    {partner}
                  </span>
                ))}
              </div>
            </div>

            <button
              onClick={() => onApplyClick(course)}
              className="btn-primary"
              style={{ width: '100%', padding: '0.9rem', fontSize: '0.95rem' }}
            >
              Lock Fee & Apply Now <ArrowRight size={16} />
            </button>
          </div>

        </div>

      </div>
    </section>
  );
}
